import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react";
import type { MobilityAd } from "@mobility-item-page/types";
import { Alert, Button, Modal, TextField, Toggle } from "@warp-ds/react";
import { type SubmitEvent, useEffect, useState } from "react";
import { useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { FeatureIds } from "src/types/unleash.ts";
import { Icon } from "../Icon.tsx";

const Saving = () => {
  return (
    <div className="animate-inprogress text-center text-m">
      <Trans
        id="itemPageLayout.signupAlert.submitstate.inprogress"
        message="Submitting..."
        comment="Text shown while asyncronous operation to submit signup form is ongoing"
      />
    </div>
  );
};

type Props = {
  adViewType: MobilityAd["adViewType"];
};

export const SignupAlert = ({ adViewType }: Props) => {
  const { featureToggles, isMobile } = useAppConfig();
  const { adId } = useAdViewMeta();
  const [showAlert, setShowAlert] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saveState, setSaveState] = useState<"none" | "invalid" | "saving" | "success" | "error">("none");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Check if the user has already signed up or dismissed the alert
    const hasSignedUp = localStorage.getItem("user-panel-signup-done");
    if (featureToggles[FeatureIds.USER_PANEL_SIGNUP] && !hasSignedUp) {
      setShowAlert(true);
    }
  }, [featureToggles]);

  const toggleModal = () => {
    if (saveState !== "saving") {
      setOpenModal(!openModal);
    }
  };
  const toggleAlert = () => {
    setShowAlert(!showAlert);
    localStorage.setItem("user-panel-signup-done", "true");
  };
  const handleSubmit = async (event: SubmitEvent) => {
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    if (!checked || !data.name || !data.email) {
      setSaveState("invalid");
      event.preventDefault();
      return;
    }
    event.preventDefault();
    setSaveState("saving");
    try {
      const response = await fetch("/mobility/api/user-panel/signup", {
        method: "POST",
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          device: isMobile ? "mobile" : "desktop",
          adViewType: adViewType,
          adId: adId,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.ok && response.status === 200) {
        setSaveState("success");
        localStorage.setItem("user-panel-signup-done", "true");
      } else setSaveState("error");
    } catch (_e) {
      setSaveState("error");
    }
  };

  const modalTitle = i18n._({
    id: "itemPageLayout.signupAlert.modalTitle",
    message: "Help us improve Finn",
    comment: "Title of the modal shown when user clicks on the signup button",
  });
  const nameLabel = i18n._({
    id: "itemPageLayout.signupAlert.nameLabel",
    message: "Name",
    comment: "Label for the name input field in the signup modal",
  });
  const namePlaceholder = i18n._({
    id: "itemPageLayout.signupAlert.namePlaceholder",
    message: "Ola Nordmann",
    comment: "Placeholder text for the name input field in the signup modal",
  });
  const emailLabel = i18n._({
    id: "itemPageLayout.signupAlert.emailLabel",
    message: "Email",
    comment: "Label for the email input field in the signup modal",
  });
  const emailPlaceholder = i18n._({
    id: "itemPageLayout.signupAlert.emailPlaceholder",
    message: "ola@nordmann.no",
    comment: "Placeholder text for the email input field in the signup modal",
  });
  const termsLabel = i18n._({
    id: "itemPageLayout.signupAlert.termsLabel",
    message: "I consent to the storage of my submitted information so that I can be contacted",
    comment: "Label for the terms and conditions checkbox in the signup modal",
  });
  const termsHelpText = i18n._({
    id: "itemPageLayout.signupAlert.termsHelpText",
    message: "We need your consent to store your information and contact you.",
    comment: "Help text shown when the terms and conditions checkbox is not checked",
  });

  return (
    <Alert show={showAlert} type="info" className="mt-16 text-m">
      <div className="flex">
        <div className="flex-1">
          <p className="text-m">
            <Trans
              id="itemPageLayout.signupAlert.alertBoxText"
              message="Would you like to help us make this site even better? Participate in a short online interview - we will reward your participation with 400 NOK."
              comment="Text shown in the alert box on item page to encourage users to sign up for a short online interview"
            />
          </p>
        </div>
        <div className="relative">
          <Button quiet onClick={toggleAlert}>
            <Icon name="Close" />
          </Button>
        </div>
      </div>
      <Button link onClick={toggleModal}>
        <Trans
          id="itemPageLayout.signupAlert.signupButtonText"
          message="Sign up here"
          comment="Text shown on the button to open the signup modal"
        />
      </Button>

      <Modal
        left={true}
        right
        open={openModal}
        onDismiss={toggleModal}
        title={modalTitle}
        style={{
          //@ts-expect-error - this is a custom prop
          "--w-modal-max-height": "fit-content",
          "--w-modal-height": "100%",
        }}
      >
        {saveState === "none" || saveState === "invalid" ? (
          <form onSubmit={handleSubmit}>
            <p className="text-m">
              <Trans
                id="itemPageLayout.signupAlert.signupFormText.part1"
                message="Sign up for a short online call to help us improve our item page and contact options - making your experience more transparent."
                comment="Main text describing what signing up will entail in terms of both the interview and the reward for participating. Part 1"
              />
              <br />
              <br />
              <Trans
                id="itemPageLayout.signupAlert.signupFormText.part2"
                message="We'll follow up with more details and a link to book a time that suits you. As thanks, you'll receive a 400 NOK GoGift gift card. The interview will be conducted in English."
                comment="Main text describing what signing up will entail in terms of both the interview and the reward for participating. Part 2"
              />
            </p>
            <TextField required className="mt-16" placeholder={namePlaceholder} label={nameLabel} name="name" />
            <TextField
              required
              className="mt-16"
              placeholder={emailPlaceholder}
              label={emailLabel}
              type="email"
              name="email"
            />
            <Toggle
              type="checkbox"
              className="mt-16"
              label={termsLabel}
              invalid={!checked && saveState === "invalid"}
              helpText={saveState === "invalid" ? termsHelpText : ""}
              onChange={(check) => {
                setChecked(check);
                setSaveState("none");
              }}
            ></Toggle>
            <div className="flex justify-end">
              <Button primary type="submit" className="mr-0 mt-16">
                <Trans id="itemPageLayout.signupAlert.submitButton" message="Sign me up" comment="Signup button text" />
              </Button>
            </div>
          </form>
        ) : (
          <></>
        )}
        {saveState === "saving" ? <Saving /> : <></>}
        {saveState === "success" ? (
          <>
            <div className="text-m mt-16">
              <Trans
                id="itemPageLayout.signupAlert.successMessage"
                message="Thank you for signing up! We will contact you shortly."
                comment="Success message displayed when a user has successfully signed up for the user panel"
              />
            </div>
            <Button primary className="mt-16" onClick={toggleModal}>
              <Trans
                id="itemPageLayout.signupAlert.closeButton"
                message="Close"
                comment="Text shown on the close button of the signup modal"
              />
            </Button>
          </>
        ) : (
          <></>
        )}
        {saveState === "error" ? (
          <>
            <div className="text-m mt-16">
              <Trans
                id="itemPageLayout.signupAlert.failureMessage"
                message="Oops! Something went wrong."
                comment="Failure message displayed when a user attempted to signup for the user panel, but something failed (techical error)"
              />
            </div>
            <Button primary className="mt-16" onClick={toggleModal}>
              <Trans
                id="itemPageLayout.signupAlert.closeButton"
                message="Close"
                comment="Text shown on the close button of the signup modal"
              />
            </Button>
          </>
        ) : (
          <></>
        )}
      </Modal>
    </Alert>
  );
};
